// Customizable Area Start
import { Message } from "../../../framework/src/Message";
import { BlockComponent } from "../../../framework/src/BlockComponent";
import { runEngine } from "../../../framework/src/RunEngine";
import MessageEnum, {
  getName,
} from "../../../framework/src/Messages/MessageEnum";
import React from "react";
export const configJSON = require("./config");

interface IEmailSuccessLoginParams {
  token: string;
  account_id: number;
  first_name: string;
  last_name: string;
  first_login: boolean;
  refreshToken: string;
  cometAuthToken: string;
}

interface IPhoneVerificationValues {
  phone_number: string;
  pin: string;
}

interface IPhoneVerificationSuccessResponse {
  message: string;
  token: string;
  account_id: number;
  first_login: boolean;
  first_name: string;
  last_name: string;
  refresh_token: string;
  comet_chat_auth_token: string;
  comet_chat_uid: string;
}

interface IPhoneVerificationErrorResponse {
  error: string;
}

interface IPhoneOtpSuccessResponse {
  message: string;
}

interface IPhoneOtpErrorResponse {
  errors: string[];
}
// Customizable Area End

export interface Props {
  // Customizable Area Start
  navigation: any;
  isOpen: boolean;
  phoneNumber: string;
  handleClose: () => void;
  handleFullPageLoader: (isLoading: boolean) => void;
  handleSuccessPhoneVerified: (loginDetails: IEmailSuccessLoginParams) => void;
  // Customizable Area End
}

export interface S {
  // Customizable Area Start
  otpFields: string[];
  otpError: string;
  remainingTime: number;
  isSendingOtp: boolean;
  isVerifyingPhone: boolean;
  // Customizable Area End
}

export interface SS {
  // Customizable Area Start
  id: any;
  // Customizable Area End
}

export default class TwoFAConfirmationController extends BlockComponent<
  Props,
  S,
  SS
> {
  // Customizable Area Start
  DEFAULT_RESEND_TIME: number = 60;
  COUNTRY_CODE_PREFIX: string = "+91";
  sendPhoneOtpApiCallId: string = "";
  verifyPhoneOtpApiCallId: string = "";
  resendTimer: number | null = null;
  inputRefs: React.RefObject<HTMLInputElement>[];
  // Customizable Area End

  constructor(props: Props) {
    super(props);
    this.subScribedMessages = [
      // Customizable Area Start
      getName(MessageEnum.RestAPIResponceMessage),
      getName(MessageEnum.NavigationPayLoadMessage),
      // Customizable Area End
    ];

    this.receive = this.receive.bind(this);

    runEngine.attachBuildingBlock(this, this.subScribedMessages);

    // Customizable Area Start
    this.state = {
      otpFields: ["", "", "", ""],
      otpError: "",
      isSendingOtp: false,
      isVerifyingPhone: false,
      remainingTime: 0,
    };

    this.inputRefs = Array(4)
      .fill(null)
      .map(() => React.createRef<HTMLInputElement>());
    // Customizable Area End
  }

  async receive(from: String, message: Message) {
    // Customizable Area Start
    const apiRequestCallId = message.getData(
      getName(MessageEnum.RestAPIResponceDataMessage)
    );

    const responseJson = message.getData(
      getName(MessageEnum.RestAPIResponceSuccessMessage)
    );

    if (apiRequestCallId === this.verifyPhoneOtpApiCallId) {
      this.handleVerifyOtpResponse(responseJson);
    } else if (apiRequestCallId === this.sendPhoneOtpApiCallId) {
      this.handleSendOtpResponse(responseJson);
    }
    // Customizable Area End
  }

  // Customizable Area Start
  async componentWillUnmount() {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }
  }

  handleVerifyOtpResponse = (
    responseJson:
      | IPhoneVerificationSuccessResponse
      | IPhoneVerificationErrorResponse
  ) => {
    if ("token" in responseJson) {
      const {
        account_id,
        token,
        refresh_token,
        first_login,
        first_name,
        last_name,
        comet_chat_auth_token,
      } = responseJson;
      this.setState({ isVerifyingPhone: false, otpError: "" });
      const loginDetails: IEmailSuccessLoginParams = {
        token,
        account_id,
        first_login,
        first_name,
        last_name,
        refreshToken: refresh_token,
        cometAuthToken: comet_chat_auth_token,
      };
      this.props.handleSuccessPhoneVerified(loginDetails);
    } else {
      this.props.handleFullPageLoader(false);
      this.setState({ otpError: responseJson.error, isVerifyingPhone: false });
    }
  };

  handleSendOtpResponse = (
    responseJson: IPhoneOtpSuccessResponse | IPhoneOtpErrorResponse
  ) => {
    if ("message" in responseJson) {
      this.setState({ isSendingOtp: false, otpError: "" });
    } else {
      this.setState({ otpError: responseJson.errors[0], isSendingOtp: false });
    }
  };

  handleOtpChange = (index: number, event: React.ChangeEvent) => {
    const inputEvent = event as React.ChangeEvent<HTMLInputElement>;
    const { value } = inputEvent.target;
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...this.state.otpFields];
    newOtp[index] = value;
    this.setState({ otpFields: newOtp });

    if (value && index < 3) {
      this.inputRefs[index + 1].current?.focus();
    }
  };

  handleOtpKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.key === "Backspace") {
      const newOtp = [...this.state.otpFields];
      if (newOtp[index]) {
        newOtp[index] = "";
      } else if (index > 0) {
        newOtp[index - 1] = "";
        this.inputRefs[index - 1].current?.focus();
      }
      this.setState({ otpFields: newOtp, otpError: "" });
    }
  };

  sendPhoneOtpApi = (phoneNumber: string) => {
    const httpBody = {
      full_phone_number: phoneNumber,
    };

    const header = {
      "Content-Type": configJSON.apiVerifyOtpContentType,
    };

    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );

    this.sendPhoneOtpApiCallId = requestMessage.messageId;

    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      configJSON.sendPhonOtpApiEndPoint
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify(header)
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestBodyMessage),
      JSON.stringify(httpBody)
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpPostMethod
    );

    runEngine.sendMessage(requestMessage.id, requestMessage);
    return true;
  };

  submitPhoneOtpApi = (phoneVerificationDetails: IPhoneVerificationValues) => {
    this.setState({ isVerifyingPhone: true });
    const httpBody = { ...phoneVerificationDetails };

    const header = {
      "Content-Type": configJSON.apiVerifyOtpContentType,
    };

    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );

    this.verifyPhoneOtpApiCallId = requestMessage.messageId;

    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      configJSON.verifyPhonOtpApiEndPoint
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify(header)
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestBodyMessage),
      JSON.stringify(httpBody)
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpPostMethod
    );

    runEngine.sendMessage(requestMessage.id, requestMessage);
    return true;
  };

  formatTime = (time: number) => {
    return String(time).padStart(2, "0");
  };

  startResendCountdown = () => {
    this.setState({ remainingTime: this.DEFAULT_RESEND_TIME });

    this.resendTimer = window.setInterval(() => {
      this.setState((prevState) => {
        if (prevState.remainingTime <= 1) {
          clearInterval(this.resendTimer!);
          return { remainingTime: 0 };
        }
        return { remainingTime: prevState.remainingTime - 1 };
      });
    }, 1000);
  };

  handleSendOtp = () => {
    const { phoneNumber } = this.props;
    const phoneWithCC = `${this.COUNTRY_CODE_PREFIX}${phoneNumber}`;
    this.setState({ isSendingOtp: true });

    this.startResendCountdown();
    this.sendPhoneOtpApi(phoneWithCC);
  };

  handleSubmitOtp = () => {
    const { phoneNumber } = this.props;
    const { otpFields } = this.state;
    const phoneVerificationDetails = {
      phone_number: phoneNumber,
      pin: otpFields.join(""),
    };
    this.props.handleFullPageLoader(true);
    this.submitPhoneOtpApi(phoneVerificationDetails);
  };

  handleContactUsRedirection = () => {
    const navigation = new Message(getName(MessageEnum.NavigationMessage));
    navigation.addData(
      getName(MessageEnum.NavigationTargetMessage),
      "Contactus"
    );
    navigation.addData(getName(MessageEnum.NavigationPropsMessage), this.props);
    this.send(navigation);
  };
  // Customizable Area End
}
